/*
 * Copyright(c) 2023 Mozanta Technologies Private Ltd.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Mozanta ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Mozanta.
 *
 * @author Indrajith C
 */

import {
  PromotionalBannerProperties,
  CountryData,
  WhatsappLinks,
  PromotionalBannerTypes,
  GeoLocation,
  AddressType,
  AddressValidationStatus,
} from "../common/lib/types";

export const constants = {
  REORDER_CL_PARAM: "reorder",
  ONLY_FRAME: "ONLY_FRAME",
  INTERIM_OMS: "interiemOms",
};

/** Console filter prefix for checkoutComplete / minimum-cart debugging (keep in sync with magrabi-storefront). */
export const CHECKOUT_COMPLETE_DEBUG_LOG = "[CheckoutCompleteDebug]";

export const countries: { code: string; name: string; shortName: string; alpha3Code: string }[] = [
  { code: "ae", alpha3Code: "uae", name: "United Arab Emirates", shortName: "UAE" },
  { code: "sa", alpha3Code: "ksa", name: "Kingdom of Saudi Arabia", shortName: "KSA" },
  { code: "qa", alpha3Code: "qat", name: "Qatar", shortName: "Qatar" },
  { code: "kw", alpha3Code: "kwt", name: "Kuwait", shortName: "Kuwait" },
  { code: "eg", alpha3Code: "egy", name: "Egypt", shortName: "Egypt" },
];

// Country code to full country name mapping for inquiry forms
export const inquiryCountryMapping: Record<string, string> = {
  ae: "UAE",
  sa: "KSA",
  qa: "QATAR",
  kw: "KUWAIT",
  eg: "EGYPT",
};

export const languages: { code: string; name: string }[] = [
  { code: "en", name: "English" },
  { code: "ar", name: "العربية" },
];

export const cookieKeys = {
  HAS_SHOWN_HOME_OVERLAY: "860bd011-deb6-5005-96f6-725e1a44ee55",
  HAS_SHOWN_NOTIFICATION: "3fa7ada3-000b-4619-b12a-ada2e50baa49",
  DRAFT_MODE: "__prerender_bypass",
  PLP_MOBILE_VIEW: "plpMobileView",
};

export const comparisonOperators = {
  GTE: ">=",
  LTE: "<=",
};

export type AttributeTypes =
  | "price"
  | "shape"
  | "frameType"
  | "fit"
  | "frameColor"
  | "material"
  | "acPrdMatEnumCode"
  | "categoryFilter"
  | "features"
  | "brands"
  | "lensColor"
  | "clColor"
  | "packFilter"
  | "disposabilityFilter"
  | "focalTypeFilter"
  | "accesoriesTypeFilter"
  | "genderFilter"
  | "vtoFilter"
  | "uvProtection"
  | "clDuration"
  | "polarized"
  | "accGender"
  | "accColor"
  | "accVolume"
  | "uvBlock"
  | "clMaterial"
  | "geo_fit"
  | "frame_lens_material"
  | "photochromic"
  | "gradient"
  | "anti_reflective"
  | "printed"
  | "blue_light_cut"
  | "solid_color"
  | "clipon_framecolor"
  | "clipon_lenscolor"
  | "clipon_style"
  | "hard_coating"
  | "uv_block"
  | "material_composition"
  | "frame_finish";

// TO-DO add to the CMS plpWidgetContents config
export const configuredFacetList: AttributeTypes[] = ["brands", "shape", "frameColor"];

export const localeConstants = {
  NEXT_LOCALE: "NEXT_LOCALE",
};

export const pageTypes = {
  HOME: "HOME",
  CART: "CART",
  WISHLIST: "WISHLIST",
  CHECKOUT: "CHECKOUT",
  PAYMENT: "CHECKOUT_PAYMENT",
  PLP: "PLP",
  CLP: "CLP",
  BLP: "BLP",
  PDP: "PDP",
  SP: "SALE_PAGE",
  BLOGS: "BLOGS",
  GUIDES: "GUIDES",
  BDP: "BLOG_DETAILS_PAGE",
  STATIC: "STATIC_PAGE",
  SEARCH: "SEARCH",
  ORDER_CONFIRMATION: "ORDER_CONFIRMATION",
  MY_ACCOUNT: "MY_ACCOUNT",
  BRANDS: "BRANDS",
  FIND_STORE: "FIND_STORE",
  ORDER_TRACKING: "ORDER_TRACKING",
  ORDER_RETURN: "ORDER_RETURN",
  ORDER_PRESCRIPTION: "ORDER_PRESCRIPTION",
  FORGOT_PASSWORD: "FORGOT_PASSWORD",
  ANY: "ANY",
  NOT_FOUND: "NOT_FOUND",
  BOOK_EYE_TEST: "BOOK_EYE_TEST",
  CONTACT_US: "CONTACT_US",
  INQUIRY: "INQUIRY",
  GUIDE_PAGE: "GUIDE_PAGE",
};

export const cloudinaryImageTypes = {
  BRAND: "b",
};

export const deviceTypes = {
  MOBILE: "mobile",
  TABLET: "tablet",
  DESKTOP: "desktop",
};

export const plpLayoutConfig = {
  WIDTH_THRESHOLDS: {
    LARGE: 1200,
    MEDIUM: 768,
  },
  PRODUCTS_PER_ROW: {
    LARGE: 4,
    MEDIUM: 3,
    SMALL: 2,
  },
  VISIBLE_HITS: {
    LARGE: { default: 20, scroll: 16 },
    MEDIUM: { default: 21, scroll: 15 },
    SMALL: { default: 10, scroll: 26 },
  },
  TILES: {
    SINGLE: "single",
    FULL_WIDTH: "fullWidth",
    HALF_WIDTH: "halfWidth",
  },
  POSITIONS: {
    RIGHT: "right",
    LEFT: "left",
  },
  VIEW: {
    COLUMN: "column-view",
    GRID: "grid-view",
  },
};

export const pdpLayoutConfig = {
  WIDTH_THRESHOLDS: {
    MEDIUM: 768,
  },
};

export const orderSummaryStates = {
  CART: "CART",
  CHECKOUT: "CHECKOUT",
  ORDER_CONFIRMATION: "ORDER_CONFIRMATION",
};

export const promotionType = {
  BUYXGETY: "BUYXGETY",
  ORDER: "ORDER",
  CATALOGUE: "CATALOGUE",
};

export const cartErrors = {
  INSUFFICIENT_STOCK: "CheckoutLineProblemInsufficientStock",
  IS_RETURNABLE: "isReturnableInt",
  INSUFFICIENT_STOCK_ERROR_CODE: "INSUFFICIENT_STOCK",
  PRODUCT_UNAVAILABLE_FOR_PURCHASE_ERROR_CODE: "PRODUCT_UNAVAILABLE_FOR_PURCHASE",
  QUANTITY_GREATER_THAN_LIMIT_ERROR_CODE: "QUANTITY_GREATER_THAN_LIMIT",
  IS_RETURNABLE_TRUE_VALUE: "Returnable: Yes",
  NOTFOUND: "NOT_FOUND",
};

export const orderSummaryErrors = {
  CODE_INVALID: "INVALID",
  CODE_INVALID_FOR_EXCLUSIVE_BRAND: "CODE_INVALID_FOR_EXCLUSIVE_BRAND",
  VOUCHER_EXPIRED: "VOUCHER_EXPIRED",
  VOUCHER_ALREADY_USED: "VOUCHER_ALREADY_USED",
  NOT_FOUND: "NOT_FOUND",
};

export const cartConstants = {
  GIFT_MESSAGE_MAXIMUM_LIMIT: 256,
  ORDER_CANCEL_REASON_MESSAGE_MAXIMUM_LIMIT: 256,
  ORDER_RETURN_REASON_MESSAGE_MAXIMUM_LIMIT: 256,
};

export const cartIds = {
  ae: "68caf27d-c200-43ed-8324-ffe3996ac1ea",
  sa: "faeeadbc-cdc1-4e79-a3cc-d576c02b69d0",
  eg: "78acc397-3bf2-4f8d-ae45-bbde3925c04e",
  qa: "385388f2-d540-4864-b2c0-242148338c48",
  kw: "d651c400-7608-44f5-8376-7c7e24028e0a",
};

export const wishlistIds = {
  ae: "3ab692bb-7606-4012-88bc-0ff2c5f313f7",
  sa: "88651bf3-14bb-49c2-9e38-f544106de6b5",
  eg: "9408a570-53b0-4a51-a3bd-49baa3011eb4",
  kw: "38355a7b-a63f-4f4e-b276-cf379a452855",
  qa: "314bcb12-a4da-4e5d-b467-1cc99386ce11",
};

export const analyticKeyIds = {
  analyticUserJourney: "b9e7f6a2-4b30-42cd-9e5d-8c1f7a2d3b1a",
  isPaymentInitiated: "a0f7764a-9292-4020-aeba-2d75cc2550c7",
};

export const addressCountryMappings: Record<string, CountryData> = {
  sa: {
    dialCode: "+966",
    name: {
      en: "Saudi Arabia",
      ar: "المملكة العربية السعودية",
    },
    phoneNumberPattern: {
      FORMAT_3_DIGIT: "999-999-9999",
      FORMAT_2_DIGIT: "99-999-9999",
    },
  },
  ae: {
    dialCode: "+971",
    name: {
      en: "United Arab Emirates",
      ar: "الإمارات العربية المتحدة",
    },
    phoneNumberPattern: {
      FORMAT_3_DIGIT: "999-999-9999",
      FORMAT_2_DIGIT: "99-999-9999",
    },
  },
  eg: {
    dialCode: "+20",
    name: {
      en: "Egypt",
      ar: "مصر",
    },
    phoneNumberPattern: {
      FORMAT_3_DIGIT: "999-999-99999",
      FORMAT_2_DIGIT: "99-999-99999",
    },
  },
  qa: {
    dialCode: "+974",
    name: {
      en: "Qatar",
      ar: "دولة قطر",
    },
    phoneNumberPattern: {
      FORMAT_3_DIGIT: "999-999-999",
      FORMAT_2_DIGIT: "99-999-999",
    },
  },
  kw: {
    dialCode: "+965",
    name: {
      en: "Kuwait",
      ar: "الكويت",
    },
    phoneNumberPattern: {
      FORMAT_3_DIGIT: "999-999-999",
      FORMAT_2_DIGIT: "99-999-999",
    },
  },
};

export const addressTypes: { [key: string]: AddressType } = {
  Home: "Home",
  Office: "Office",
};

export const ADDRESS_VALIDATION_STATUS: Record<string, AddressValidationStatus> = {
  PENDING: "PENDING" as AddressValidationStatus,
  VERIFIED: "VERIFIED" as AddressValidationStatus,
};

export const whatsappLinks: WhatsappLinks = {
  ae: "https://api.whatsapp.com/send/?phone=966538653969&text&type=phone_number&app_absent=0",
  kw: "https://api.whatsapp.com/send/?phone=966538653969&text&type=phone_number&app_absent=0",
  eg: "https://api.whatsapp.com/send/?phone=966538653969&text&type=phone_number&app_absent=0",
  sa: "https://api.whatsapp.com/send/?phone=966538653969&text&type=phone_number&app_absent=0",
  qa: "https://api.whatsapp.com/send/?phone=966538653969&text&type=phone_number&app_absent=0",
};

export const FALLBACK_WHATSAPP_LINKS: WhatsappLinks = whatsappLinks;

/**
 * Generate WhatsApp link from phone number
 * @param whatsappNumber - Phone number (digits only, e.g., 966538653969)
 * @returns WhatsApp API link or null
 */
export const generateWhatsappLink = (whatsappNumber?: string): string | null => {
  if (!whatsappNumber) {
    return null;
  }
  return `https://api.whatsapp.com/send/?phone=${whatsappNumber}&text&type=phone_number&app_absent=0`;
};

export const ProductTypes = {
  sunglasses: "sunglasses",
  ophthalmicLens: "ophthalmic-lens",
  eyeglasses: "eyeglasses",
  contactLens: "contact-lens",
  accessories: "accessories",
};

export const parentCategories = ["contactlenses", "accessories"];

export const pdpContent = {
  IMAGE: "image",
  VIDEO: "video",
  LEFT: "left",
  RIGHT: "right",
};

export const productDetails = {
  PRODUCT_DETAILS: "productDetails",
  SIZE_AND_FIT: "sizeAndFit",
  ABOUT: "about",
  GIFT_PACKAGING: "giftPackaging",
  SHIPPING_AND_RETURN: "shippingAndReturn",
  CUSTOM_TAB: "customTab",
  GUIDE_TAB: "guideTab",
  CARE_TAB: "careTab",
  FAQ_TAB: "faqTab",
  TAB: "tab",
  ACTIVE: "active",
};

export const pdpUSPTypes = {
  SHIPPING_TIMELINE: "shippingTimeline",
  GIFT: "gift",
  SHIPPING_COST: "shippingCost",
  PAYMENT_METHOD: "paymentMethod",
  COUNTRY_FEATURE: "countryFeature",
  CATEGORY_FEATURE: "categoryFeature",
};

export const recommendationType = {
  CUSTOM: "custom",
  CUSTOM_BASE: "customBase",
  CUSTOM_INTERMEDIATE: "customIntermediate",
  SALEOR_COLLECTION: "saleorCollection",
  RELATED: "related",
  SIMILAR: "similar",
  TRENDING: "trending",
  BOUGHT_TOGETHER: "boughtTogether",
  MAX_RECOMMENDATIONS: 25,
  MIN_RECOMMENDATIONS: 5,
  MIN_RECOMMENDATIONS_WITH_PROMOTION_TILE: 4,
};

export const GENERIC_PAYMENT_ERROR_MESSAGE = "Something went wrong. Please try again";

export const paymentConstants = {
  gateways: {
    CHECKOUT: "app.saleor.checkout",
    TABBY: "app.saleor.tabby",
    TAMARA: "app.saleor.tamara",
    PAYMOB: "app.saleor.paymob",
  },
  statuses: {
    DECLINED: "Declined",
  },
  transactionEventTypes: {
    AUTHORIZATION_SUCCESS: "AUTHORIZATION_SUCCESS",
    AUTHORIZATION_ACTION_REQUIRED: "AUTHORIZATION_ACTION_REQUIRED",
    CHARGE_ACTION_REQUIRED: "CHARGE_ACTION_REQUIRED",
    CHARGE_SUCCESS: "CHARGE_SUCCESS",
  },
  paymentTypes: {
    CASH: "Cash",
  },
  paymentMethods: {
    CARD: "card",
    PAYMOB_CARD: "paymob-card",
    VALU: "valu",
    COD: "cod",
    APPLE_PAY: "applePay",
    GPAY: "googlePay",
    TAMARA: "tamara",
    TABBY: "tabby",
  },
  threshold: {
    card: { MIN: "cardThresholdMin", MAX: "cardThresholdMax" },
    "paymob-card": { MIN: "cardThresholdMin", MAX: "cardThresholdMax" },
    valu: { MIN: "valuThresholdMin", MAX: "valuThresholdMax" },
    cod: { MIN: "min", MAX: "max" },
    applePay: { MIN: "applePayThresholdMin", MAX: "applePayThresholdMax" },
    googlePay: { MIN: "googlePayThresholdMin", MAX: "googlePayThresholdMax" },
    tamara: { MIN: "thresholdMin", MAX: "thresholdMax" },
    tabby: { MIN: "thresholdMin", MAX: "thresholdMax" },
  } as { [key: string]: { MIN: string; MAX: string } },
  PROTOCOL_VERSION: "ECv1",
  LIVE: "live",
  TEST: "test",
  ENV_PRODUCTION: "PRODUCTION",
  ENV_TEST: "TEST",
};

export const checkoutSteps = {
  CHECKOUT_SHIPPING: "CHECKOUT_SHIPPING",
  CHECKOUT_PAYMENT: "CHECKOUT_PAYMENT",
  CHECKOUT_REVIEW: "CHECKOUT_REVIEW",
};

export const promotionalBannerType = {
  SINGLE_COL_LARGE_BANNER: "singleColLargeBanner",
  SINGLE_COL_LARGE_BANNER_MOBILE: "singleColLargeBannerMobile",
  SINGLE_COL_MEDIUM_BANNER: "singleColMediumBanner",
  SINGLE_COL_MEDIUM_BANNER_MOBILE: "singleColMediumBannerMobile",
  SINGLE_COL_SMALL_BANNER: "singleColSmallBanner",
  TWO_COL_BANNER: "twoColBanner",
  TWO_COL_BANNER_MOBILE: "twoColBannerMobile",
  THREE_COL_BANNER: "threeColBanner",
  MIXED_THREE_COL_BANNER: "mixedThreeColBanner",
  FOUR_COL_BANNER: "fourColBanner",
};

export const bannerProperties: Record<PromotionalBannerTypes, PromotionalBannerProperties> = {
  singleColLargeBanner: {
    imageWidth: 1344,
    imageHeight: 660,
    aspectRatio: "2.04/1",
    className: "col-span-12",
  },
  singleColMediumBanner: {
    imageWidth: 1344,
    imageHeight: 492,
    aspectRatio: "2.73/1",
    className: "col-span-12",
  },
  singleColSmallBanner: {
    imageWidth: 1344,
    imageHeight: 448,
    aspectRatio: "3/1",
    className: "col-span-12",
  },
  twoColBanner: {
    imageWidth: 660,
    imageHeight: 660,
    aspectRatio: "1/1",
    className: "col-span-6",
  },
  threeColBanner: {
    imageWidth: 432,
    imageHeight: 681,
    aspectRatio: "0.63/1",
    className: "col-span-4",
  },
  mixedThreeColBannerhalfWidthBanner: {
    imageWidth: 660,
    imageHeight: 521,
    aspectRatio: "1.27/1",
    className: "md:col-span-6",
  },
  mixedThreeColBannersingleTileBanner: {
    imageWidth: 318,
    imageHeight: 521,
    aspectRatio: "aspect-[0.61/1]",
    className: "md:col-span-3",
  },
  fourColBanner: {
    imageWidth: 318,
    imageHeight: 521,
    aspectRatio: "0.61/1",
    className: "md:col-span-3",
  },
  singleColLargeBannerMobile: {
    imageWidth: 343,
    imageHeight: 402,
    aspectRatio: "0.85/1",
    className: "col-span-12",
  },
  singleColMediumBannerMobile: {
    imageWidth: 343,
    imageHeight: 343,
    aspectRatio: "1/1",
    className: "col-span-12",
  },
  twoColBannerMobile: {
    imageWidth: 165,
    imageHeight: 199,
    aspectRatio: "0.83/1",
    className: "col-span-6",
  },
};

export enum pageSections {
  HERO_BANNER = "homeHeroBanner",
  BRAND_CAROUSEL = "brandCarousel",
  PROMOTIONAL_BANNER = "promotionalBanner",
  PROMO_BANNER_GROUPS = "promoBannerGroups",
  RECOMMENDATIONS = "homeRecommendation",
  BANNER_GRID = "bannerGrid",
  PDP_BANNER_GRID = "pdpBannerGrid",
  PAGE_SECTION = "pageSection",
  BLOG_AND_STORE_FINDER = "blogAndStoreFinder",
  FIND_A_STORE = "findAStore",
  SALE_PAGE_HERO_BANNER = "saleHeroBanner",
  SALE_PAGE_EDITS = "salePageSectionEdits",
  STATIC_PAGE_DESCRIPTION = "descriptionSection",
  STATIC_PAGE_FAQ = "faqSection",
  STATIC_PAGE_TIMELINE = "timelineSection",
  STATIC_PAGE_PEOPLE = "peopleSection",
  STATIC_PAGE_QUOTE = "quoteSection",
  STATIC_PAGE_IMAGE_GRID = "imageGridSection",
  STATIC_PAGE_CENTERED_DESCRIPTION = "centeredDescription",
  STATIC_PAGE_HORIZONTAL_LINE = "horizontalLine",
  STATIC_PAGE_IMAGE_GRID_WITH_DESC = "imageGridWithTitleAndDescriptionSection",
  STATIC_PAGE_REFER_A_FRIEND_CAROUSEL = "referAFriendCarousal",
  STATIC_PAGE_REFER_A_FRIEND_GRID = "referAFriendGrid",
  STATIC_PAGE_GROUPED_FAQ_SECTION = "groupedFaqSection",
  STATIC_PAGE_CTA_SECTION = "staticCtaSection",
  STATIC_VIDEO_SECTION = "videoSection",
  STATIC_IMAGE_SECTION = "imageSection",
  BLOG_PAGE_DESCRIPTION = "descriptionSection",
  BLOG_PAGE_RECOMMENDATION = "blogRecommendation",
  BLOG_PAGE_HERO_BANNER = "blogPageHeroBanner",
  BLOG_PAGE_CONTENTS = "blogPageContents",
  BLOG_PAGE_CONTINUE_READING = "blogPageContinueReadings",
  BRAND_LOGO_GRID_SECTION = "brandLogoGridSection",
}

export const mediaType = {
  IMAGE: "image",
  VIDEO: "video",
};

export enum saleorMediaType {
  MEDIA_TYPE_IMAGE = "IMAGE",
  MEDIA_TYPE_VIDEO = "VIDEO",
}

export const PDP_ZOOM_MAX_LEVEL = 6;
export const PDP_ZOOM_STEP = 0.5;
export const PDP_ZOOM_MOUNT_RANGE = 1;

export const videoExtensions = ["mp4", "webm", "mov", "mkv", "flv", "wmv", "ogg", "mov", "avi", "m4v"];

export enum PaymentGateWay {
  GATEWAY_PAYMOB = "PAYMOB",
  GATEWAY_TAMARA = "TAMARA",
  GATEWAY_TABBY = "TABBY",
  GATEWAY_CHECKOUT = "CHECKOUT.COM",
}

export enum MetaDataKeys {
  PAYMENT_INTENT = "paymentIntent",
  CHECKOUT_ID = "Checkoutid",
  PARENT_CHECKOUT_ID = "parentCheckoutid",
  PERSONAL_DETAILS = "personalDetails",
  QUERY_ID = "queryID",
}

export const sessionStorageKeys = {
  SHOWS_PROMO_AUTO_POPOVER: "showsPromoAutoPopover",
  PREVIOUS_PAGE_URL: "previousPageUrl",
  GIFT_CHECKOUT_SELECTION: "giftCheckoutSelection",
};

export enum DeliveryMethodType {
  WAREHOUSE = "Warehouse",
  SHIPPING_METHOD = "ShippingMethod",
}

export const ourBrandsFields = {
  BRAND: "brand",
  CATEGORY: "category",
  BRAND_LETTER: "brandLetter",
  CATEGORY_WORD: "categoryWord",
  CATEGORY_ALL: "All",
  CATEGORY_ALL_ARABIC: "الجميع",
  NAME: "name",
  BADGE: "badge",
  BREADCRUMBS_BRAND_EN: "Brands",
  BREADCRUMBS_BRAND_AR: "العلامات التجارية",
};

export const arAlphabets = [
  "9-0",
  "ا",
  "ب",
  "ت",
  "ث",
  "ج",
  "ح",
  "خ",
  "د",
  "ذ",
  "ر",
  "ز",
  "س",
  "ش",
  "ص",
  "ض",
  "ط",
  "ظ",
  "ع",
  "غ",
  "ف",
  "ق",
  "ك",
  "ل",
  "م",
  "ن",
  "ه",
  "و",
  "ي",
];

export const enAlphabets = [
  "0-9",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

export const monthMap: { [key: string]: string } = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December",
};

export const linkType = {
  EXTERNAL: "external",
  INTERNAL: "internal",
};

export const bannerGridType = {
  LARGE: "large",
  SMALL: "small",
};

export const bannerGridContentType = {
  PRODUCTS: "products",
  IMAGE: "image",
};

export const bannerGridMediaPosition = {
  LEFT: "left",
  RIGHT: "right",
};

export const checkoutConstants = {
  DESCRIPTION: "Description",
  FILE_NAME_ARRAYS: [
    `city_area_map_${process.env.SYSTEM_BRAND_ID ?? "magrabi"}-ae-web.json`,
    `city_area_map_${process.env.SYSTEM_BRAND_ID ?? "magrabi"}-sa-web.json`,
    `city_area_map_${process.env.SYSTEM_BRAND_ID ?? "magrabi"}-qa-web.json`,
    `city_area_map_${process.env.SYSTEM_BRAND_ID ?? "magrabi"}-eg-web.json`,
  ],
};

export const externalPluginIds = {
  AUTH: "mirumee.authentication.openidconnect",
};

export const plpTypes = {
  BRAND: "brand",
  CATEGORY: "category",
  COLLECTION: "collection",
  PLP: "PLP",
};

export enum saleorBlockType {
  PARAGRAPH = "paragraph",
  QUOTE = "quote",
  HEADER = "header",
  LIST = "list",
}

[
  "B2C_1A_DOCTORM_EMAIL_PHONE_SIGNIN_SIGNUP",
  "B2C_1A_DOCTORM_INV_GENLINK",
  "B2C_1A_DOCTORM_PASSWORD_CHANGE",
  "B2C_1A_DOCTORM_PASSWORD_RESET_REDEEM",
  "B2C_1A_DOCTORM_PROFILE_EDIT",
  "B2C_1A_DOCTORM_SIGNUP_SIGNIN",
  "B2C_1A_DOCTORM_SOCIAL_SIGNUP_SIGNIN",
  "B2C_1A_DOCTORM_TRUSTFRAMEWORKEXTENSIONS",
];

export const adb2cCustomPolicies =
  (process.env.SYSTEM_BRAND_ID || process.env.NEXT_PUBLIC_BRAND_ID || "magrabi") === "magrabi"
    ? {
        SIGNUP_SIGNIN: "B2C_1A_SIGNUP_SIGNIN",
        PHONE_SIGNIN: "B2C_1A_PHONENUMBERSIGNIN",
        EMAIL_SIGNIN: "B2C_1A_EMAILSIGNIN",
        SIGNUP: "B2C_1A_SIGNUP",
        SOCIAL_SIGNUP_SIGNIN: "B2C_1A_SOCIAL_SIGNUP_SIGNIN",
        EMAIL_PHONE_SIGNIN_SIGNUP: "B2C_1A_EMAIL_PHONE_SIGNIN_SIGNUP",
        PASSWORD_CHANGE: "B2C_1A_PASSWORD_CHANGE",
        PROFILE_EDIT: "B2C_1A_PROFILE_EDIT",
        DELETE_MY_ACCOUNT: "B2C_1A_DELETE_MY_ACCOUNT",
      }
    : {
        SIGNUP_SIGNIN: "B2C_1A_DOCTORM_SIGNUP_SIGNIN",
        PHONE_SIGNIN: "B2C_1A_PHONENUMBERSIGNIN",
        EMAIL_SIGNIN: "B2C_1A_EMAILSIGNIN",
        SIGNUP: "B2C_1A_SIGNUP",
        SOCIAL_SIGNUP_SIGNIN: "B2C_1A_DOCTORM_SOCIAL_SIGNUP_SIGNIN",
        EMAIL_PHONE_SIGNIN_SIGNUP: "B2C_1A_DOCTORM_EMAIL_PHONE_SIGNIN_SIGNUP",
        PASSWORD_CHANGE: "B2C_1A_DOCTORM_PASSWORD_CHANGE",
        PROFILE_EDIT: "B2C_1A_DOCTORM_PROFILE_EDIT",
        DELETE_MY_ACCOUNT: "B2C_1A_DELETE_MY_ACCOUNT",
      };

export const adb2cCallbackEndpoints = {
  default: "/api/auth/callback/azure-ad-b2c",
};

export const authConstants = {
  TOKEN: "token",
  REFRESH_TOKEN: "refreshToken",
  LOGIN_STATE: "login_state",
};

export const saleorPlpMobileViewType = {
  COLUMN: "Column",
  GRID: "Grid",
};

export const sessionQueryParams = {
  LOGIN_MODAL_STATUS: "sessionInfoM",
  REDIRECT: "redirect",
  FRAME: "frame",
  CHECKOUT_FLOW: "_cflow",
  LOGIN_HINT: "login_hint",
  FLOW: "flow",
  URL_JWT_TOKEN: "urlJWTToken",
  DISABLE_REDIRECT: "disableRedirect",
  LENSE_SELECTOR: "_lsct",
  IS_CHECKOUT: "_ichkt",
  // REDIRECT_PATH: "redirectPath"
};

const brandId = process.env.SYSTEM_BRAND_ID || process.env.NEXT_PUBLIC_BRAND_ID || "";

export const siteIds: {
  [key: string]: string;
} = {
  ae: `${brandId.toUpperCase()}-AE`,
  eg: `${brandId.toUpperCase()}-EG`,
  sa: `${brandId.toUpperCase()}-SA`,
  qa: `${brandId.toUpperCase()}-QA`,
};

export const atpCheckExcludedProducts = new Set(["ophthalmic_lenses"]);

export const previewConstants = {
  RECOMMENDATIONS: "recommendations",
  SECTIONS: "sections",
  ITEMS: "items",
  MEDIA_TILE: "mediaTile",
  CONTENT_TILE: "contentTile",
  IMAGES: "images",
  IMAGE: "image",
  LARGE_SCREEN_IMAGE: "largeScreenImage",
  SMALL_SCREEN_IMAGE: "smallScreenImage",
  LARGE_SCREEN_VIDEO: "largeScreenVideo",
  SMALL_SCREEN_VIDEO: "smallScreenVideo",
  BLOG_SECTION: "blogSection",
  STORE_FINDER: "storeFinder",
  BLOGS: "blogs",
  BRANDS: "brands",
  LOGO: "logo",
  MENU: "menu",
  TITLE: "title",
  DESCRIPTION: "description",
  PROMOTION_TILES: "promotionalTiles",
  VIDEOS: "videos",
  SUB_MENU: "subMenu",
  BANNER: "banner",
  PDP_BANNER: "pdpBanner",
  LARGE_DESKTOP_IMAGE: "largeDesktopImage",
  SMALL_DESKTOP_IMAGE: "smallDesktopImage",
  LARGE_MOBILE_IMAGE: "largeMobileImage",
  SMALL_MOBILE_IMAGE: "smallMobileImage",
  LARGE_DESKTOP: "largeDesktop",
  SMALL_DESKTOP: "smallDesktop",
  LARGE_MOBILE: "largeMobile",
  SMALL_MOBILE: "smallMobile",
  LARGE_SCREEN_ITEMS: "largeScreenItems",
  SMALL_SCREEN_ITEMS: "smallScreenItems",
  TYPES: "types",
};

export const federationAuthProviders = {
  FACEBOOK: "facebook.com",
  GOOGLE: "google.com",
  APPLE: "apple.com",
};

export const analyticEventTypes = {
  ACTIONS: "ACTIONS",
  DETAILS: "DETAILS",
  CUSTOM: "CUSTOM",
};

export const analyticEventSources = {
  PLP: "PLP",
  PDP: "PDP",
  RECOMMENDED_PRODUCTS: "Recommended Products",
  CART: "Cart",
  CHECKOUT: "Checkout",
  HOME: "Home Page",
  SALE_PAGE: "Sale Page",
  SEARCH: "Search Result",
  PROMOTIONAL_BANNER: "promotional_banner",
  WISHLIST: "Wish List",
};

export const analyticBuyFlows = {
  QUICK_BUY: "Quick Flow",
  STANDARD_FLOW: "Standard Flow",
};

export const editsShape = {
  CHIP: "chip",
  ROUND: "round",
  COLLAPSED: "collapsed",
  SQUARE: "square",
};

export const editsSectionType = {
  CATEGORY: "category",
  COLLECTION: "collection",
};

export const salePageConstants = {
  INITIAL_DESKTOP_PRODUCT_COUNT: 8,
  INITIAL_MOBILE_PODUCT_COUNT: 12,
  HITS_COUNT: 22,
};

export const sessionFlowConstants = {
  LOGIN_EMAIL: "email-login",
  LOGIN_PHONE: "phone-login",
  REGISTER: "register",
  GUEST: "guest",
  CHANGE_PASSWORD: "change-password",
  PROFILE_EDIT: "updateprofile",
  ABANDON_EMAIL: "cart_abandon_email",
};

export const countryCoordinates: { [key: string]: GeoLocation } = {
  ae: {
    lat: 23.424076,
    lng: 53.847818,
  },
  sa: {
    lat: 23.885942,
    lng: 45.079162,
  },
  eg: {
    lat: 26.820553,
    lng: 30.802498,
  },
  qa: {
    lat: 25.354826,
    lng: 51.183884,
  },
  kw: {
    lat: 29.31166,
    lng: 47.481766,
  },
};

export const ProductType = {
  SUNGLASSES: "sunglasses",
  EYEGLASSES: "eyeglasses",
  ACC_BY_COLOR: "accessories_bycolor",
  ACC_BY_COLOR_BY_POWER: "accessories_bycolor_bypower",
  ACC_VOLUME: "accessories_byvolume",
  CL_CLEAR: "contact_lenses_clear",
  CL_COLOR: "contact_lenses_color",
  ACC_BY_FRAMECOLOR_BY_LENSCOLOR: "accessories_byframecolor_bylenscolor",
};

export const maculaItemGroupCategoryNames = {
  EYEGLASSES: "Eyeglasses",
  OL: "Ophthalmic Lenses",
};

export const facetTypeConstant = {
  CL_COLOR: "clColor",
  FRAME_COLOR: "frameColor",
  PRICE: "price",
  VTO_FILTER: "vtoFilter",
  UV_PROTECTION: "uvProtection",
  PACK_FILTER: "packFilter",
};

export enum SaleorAttributes {
  DESCRIPTION = "description",
  FRAME_COLOR = "fcCodeEnum",
  LENS_COLOR = "lenseColor",
  FRAME_FIT = "fStyleEnum",
  GENDER = "genderEnum",
  FRAME_TYPE = "frameTypeEnum",
  FRAME_MATERIAL = "frameMaterialEnum",
  LENS_SIZE = "lensSize",
  TEMPLE_SIZE = "tSize",
  BRIDGE_SIZE = "bridgeSize",
  FRAME_LENGTH = "frameLength",
  FRAME_SHAPE = "fsShape",
  SHAPE = "shape",
  MODEL = "model",
  HARD_COATING = "hard_coating",
  UVBLOCK = "uv_block",
  POLARIZED = "polarized",
  LENS_MATERIAL = "frame_lens_material",
  MIRRORED = "mirror",
  GEO_FIT = "geo_fit",
  COLOR_CODE = "colorCode",
  ACC_COLOR = "accColorFamilyEnumCode",
  ACC_POWER = "acc_power",
  ACC_VOLUME = "volume",
  ACC_TYPE = "acPrdCatgEnumCode",
  ACC_GENDER = "acGenderEnumCode",
  ACC_MODEL = "acModel",
  ACC_MATERIAL = "acPrdMatEnumCode",
  CL_POWER = "clPower",
  CL_PER_PACK_SIZE = "clPerPackSize",
  CL_COLOR = "clColorEnum",
  CL_WATER_CONTENT = "clWater",
  CL_MATERIAL = "clMatEnum",
  CL_DIAMETER = "clDiameter",
  CL_BASE_CURVE = "clBaseCurve",
  CL_PRODUCT_IMAGE = "cl_product_image",
  CL_DURATION = "windEnum",
  CL_DISPOSABILITY = "clUsage",
  DISCLAIMER = "disclaimer",
  UV_BLOCK = "uvBlock",
  IS_RETURNABLE = "isReturnableInt",
  PRODUCT_IMAGE = "product_image",
  TITLE = "title",
  MODEL_IMAGE = "model_image",
  FEATURE_1 = "feature1",
  FEATURE_2 = "feature2",
  FEATURE_3 = "feature3",
  FEATURE_4 = "feature4",
  VTO_ENABLED = "vto_enabled",
  EAN = "sapEAN",
  PARENT_BRAND = "parent_brand",
  LENS_TECHNOLOGY = "frame_lens_coating",
  TEMP_STYLE_FOR_PAIR_EYEWEAR = "temp_clipon_style",
  PHOTOCHROMIC = "photochromic",
  GRADIENT = "gradient",
  BUNDLE_ID = "bundle_id",
  ANTI_REFLECTIVE = "anti_reflective",
  PRINTED = "printed",
  BLUE_LIGHT_CUT = "blue_light_cut",
  SOLID_COLOR = "solid_color",
  CLIPON_FRAMECOLOR = "clipon_framecolor",
  CLIPON_LENSCOLOR = "clipon_lenscolor",
  CLIPON_STYLE = "clipon_style",
  MATERIAL_COMPOSITION = "material_composition",
  FRAME_FINISH = "frame_finish",
  CLIP_ON_1 = "clip_on_1",
  CLIP_ON_2 = "clip_on_2",
  FRAME_PATTERN = "frame_pattern",
  FLEXIBLE = "flexible",
  EXPANDABLE = "expandable",
  ARMLESS = "armless",
  TEMPLE_MATERIAL = "temple_material",
  TEMPLE_TIP_MATERIAL = "temple_tip_material",
  TEMPLE_COLOR = "temple_color",
  TEMPLE_TIP_COLOR = "temple_tip_color",
  HINGES_MATERIAL = "hinges_material",
  OXYGEN_PERMEABILITY = "oxygen_permeability",
  MANUFACTURER = "manufacturer",
  MIRRORED_BOOLEAN = "mirrored",
  LENS_FEATURES = "lens_features",
  RX_ABLE = "rxable",
  PRIZM = "prizm",
  FRAME_DESIGN = "frame_design",
  THEME = "theme",
  COLLECTION_NAME = "collectionName",
  PAIR_SWATCH_IMAGE = "pair_swatch_image",
}

export const attributesWithUnits = [
  SaleorAttributes.LENS_SIZE,
  SaleorAttributes.BRIDGE_SIZE,
  SaleorAttributes.FRAME_LENGTH,
  SaleorAttributes.SHAPE,
  SaleorAttributes.TEMPLE_SIZE,
  SaleorAttributes.CL_BASE_CURVE,
  SaleorAttributes.CL_DIAMETER,
  SaleorAttributes.ACC_VOLUME,
  SaleorAttributes.CL_PER_PACK_SIZE,
  SaleorAttributes.CL_POWER,
  SaleorAttributes.ACC_POWER,
  SaleorAttributes.CL_WATER_CONTENT,
];

export enum AttributeInputTypeEnum {
  REFERENCE_ENTITY = "REFERENCE_ENTITY",
  SCOPABLE_REFERENCE_ENTITY = "SCOPPABLE",
  PLAIN_TEXT = "PLAIN_TEXT",
  RICH_TEXT = "RICH_TEXT",
  NUMERIC = "NUMERIC",
  BOOLEAN = "BOOLEAN",
  DATE = "DATE",
  DATE_TIME = "DATE_TIME",
  DROPDOWN = "DROPDOWN",
  MULTISELECT = "MULTISELECT",
}

// PDP Attribute Labels
export const PDP_ATTRIBUTE_LABELS = {
  LENS_FEATURES: {
    en: "Lens Features",
    ar: "مميزات العدسة",
  },
};

// Lens feature boolean attributes that should be grouped together
export const LENS_FEATURES_ATTRIBUTES = [
  "photochromic",
  "gradient",
  "anti_reflective",
  "printed",
  "blue_light_cut",
  "solid_color",
  "flexible",
  "mirror",
  "polarized",
  "hard_coating",
  "rxable",
  "prizm",
];

export const fitTypes = ["Narrow", "Standard", "Wide"];

export const fitInfo: Record<string, Record<string, Record<string, string>>> = {
  en: {
    sunglasses: {
      narrow: "55mm and below",
      standard: "56mm - 64mm",
      wide: "65mm and above",
    },
    eyeglasses: {
      narrow: "50mm and below",
      standard: "51mm - 54mm",
      wide: "55mm and above",
    },
  },
  ar: {
    sunglasses: {
      narrow: "55ملم أو أدناه",
      standard: "56ملم - 64ملم",
      wide: "65ملم وما فوق",
    },
    eyeglasses: {
      narrow: "50ملم أو أدناه",
      standard: "51ملم - 54ملم",
      wide: "55ملم وما فوق",
    },
  },
};

export enum lensConfigurationSteps {
  LENS_TYPE = "LENS_TYPE",
  PRESCRIPTION = "PRESCRIPTION",
  UPLOAD_PRESCRIPTION = "UPLOAD_PRESCRIPTION",
  LENS_CUSTOMIZATION = "LENS_CUSTOMIZATION",
  ADDITIONAL_OPTIONS = "ADDITIONAL_OPTIONS",
  REVIEW = "REVIEW",
  ADD_TO_CART = "ADD_TO_CART",
}

export const localizedIndexes: string[] = ["brand_asc", "brand_desc"];

export const customizableProperties: Record<string, string> = {
  DESIGN: "DESIGN",
  INDEX: "INDEX",
  TREATMENT: "TREATMENT",
  COATING: "COATING",
};

export const chatbotMappings: { [key: string]: string } = {
  kw: "magrabi_kuwait",
  qa: "magrabi_qatar",
  eg: "magrabi_egypt",
  sa: "magrabi_ksa",
  ae: "magrabi_uae",
};

export const doctormChatBotMappings: { [key: string]: string } = {
  sa: "doctorm_webchat",
  ae: "doctorm_webchat_uae",
  eg: "doctorm_webchat_egypt",
};

export const fulfillmentTypes: { [key: string]: string } = {
  HD: "HOME_DELIVERY",
  CNC: "CLICK_AND_COLLECT",
};

export const sanityPages: { [key: string]: string } = {
  HOME: "homepage",
  BRANDS: "brandsListingPage",
};

export const shippingConstants = {
  KEY_METHOD: "method",
  KEY_PERSONAL_DETAILS: "pd",
  VAL_YES: "y",
  HD: "hd",
  CNC: "cnc",
  ADDRESS_METADATA: {
    KEY_ADDRESS_ID: "addressId",
  },
  SHIPPING_TYPE: {
    WARE_HOUSE: "Warehouse",
  },
} as const;

export const productHighlightsConstants: Record<string, string> = {
  "defaultVariant.attributes.en.uvBlock": "UV Block",
  "defaultVariant.attributes.ar.uvBlock": "UV Block",
  "defaultVariant.attributes.en.polarized": "Polarized",
  "defaultVariant.attributes.ar.polarized": "Polarized",
};

export const vtoConstants = {
  attirbute: SaleorAttributes.VTO_ENABLED,
  value: "true",
};

export const prescriptionSources = {
  USER_UPLOADED: "User Uploaded",
};

export const prescriptionTypes: { [key: string]: string } = {
  SEND_LATER: "SEND_LATER",
  SAVED: "SAVED",
};

export const lcConstants = {
  INCLUDED: "INCLUDED",
};

export const eyeTestServiceType = {
  EYE_TEST: "eyeTest",
  RTA_EYE_TEST: "rtaEyeTest",
  CONTACT_LENS_FITTING: "contactLensFitting",
  EYE_WELLNESS: "eyewellness",
};

export const lensConfiguratorHeaderTitleMap: { [key: string]: string } = {
  PRESCRIPTION: "lens_config_save_prescription_section_title",
  UPLOAD_PRESCRIPTION: "lens_config_upload_prescription_section_title",
  DEFAULT: "lens_config_modal_title",
};

export const cartPrescriptionConstants: { [key: string]: string } = {
  SEND_LATER: "cart_send_later",
  SAVED: "cart_saved_prescription",
};

export const errorCodes = {
  NOT_FOUND: "404",
  SERVER_ERROR: "500",
};

export const allowedPrescriptionFileTypes = {
  PDF: "application/pdf",
  JPEG: "image/jpeg",
  JPG: "image/jpg",
  PNG: "image/png",
  HEIF: "image/heif",
  HEIC: "image/heic",
};

export const heifFileTypes: { [key: string]: string } = { heif: "image/heif", heic: "image/heic" };

export const sitemapPageTypes = {
  PRODUCT: "product",
  CATEGORY: "category",
  BRAND: "brand",
  COLLECTION: "collection",
  SALE: "sale",
  STATIC: "static",
  PRODUCT_VARIANT: "product_variant",
  PRODUCT_SKU: "product_sku",
  BLOG: "blog",
};

export const idPrefixes = {
  PRODUCT: "Product",
  PRODUCT_VARIANT: "ProductVariant",
  CATEGORY: "Category",
  COLLECTION: "Collection",
  BRAND: "Brand",
  SALE: "Sale",
};

export const rewardValueTypes = {
  PERCENTAGE: "PERCENTAGE",
  FIXED: "FIXED",
};

export const giftTypes = {
  SAME_CATALOGUE: "SAME_CATALOGUE",
  FREE_GIFT: "FREE_GIFT",
};

export const prescriptionConstants: { [key: string]: string } = {
  SEND_LATER: "cart_send_later",
  SAVED: "cart_saved_prescription",
};

export const dateFormats = {
  DEFAULT_DATEFORMAT: "DD/MM/YYYY",
  YYYY_MM_DD: "YYYY-MM-DD",
  DD_MM_YYYY: "DD-MM-YYYY",
  DD_MMMM_YYYY: "DD, MMMM, YYYY",
  MMMM_DD_YYYY: "MMMM DD, YYYY",
  HH_MM: "HH:MM",
  HH_MM_SS: "HH_MM_SS",
  DD_PFX_MMMM_YYYY: "DD_PFX_MMMM_YYYY",
};

export const bookingStatus = {
  PENDING: "Pending",
  NOSHOW: "No Show",
  PAST: "Past",
  CANCELLED: "Cancelled",
  COMPLETED: "Completed",
};

export const bookingStatusArabic = {
  PENDING: "قيد الانتظار",
  NOSHOW: "لم يحضر",
  PAST: "سابق",
  CANCELLED: "ملغى",
  COMPLETED: "مكتمل",
};

export enum EyeTestEventActions {
  TEST_TYPE = "Test Type",
  STORE = "Store",
  DATE_AND_TIME = "Date & Time",
  MY_INFO = "My Info",
}

export enum EyeTestErrorCodes {
  BOOKING_FAILED = "BOOKING_FAILED",
  UPDATE_FAILED = "UPDATE_FAILED",
}

export const logoutRedirectUrl = "/api/auth/logout";

// This const is used to map values in cookie to reduced cookie size
export const cookieValueKeyAliases = {
  c_ae: "1",
  c_sa: "2",
  c_eg: "3",
  c_qa: "4",
  c_kw: "5",
  w_ae: "6",
  w_sa: "7",
  w_eg: "8",
  w_qa: "9",
  w_kw: "10",
} as { [key: string]: string };

export const sessionScopeCookies = [
  authConstants.TOKEN,
  authConstants.REFRESH_TOKEN,
  ...Object.keys(cookieValueKeyAliases),
];

export const plpInternalUrlQueryParams = {
  QUERY_KEY: "_p-l",
  QUERY_VALUE: "d",
  TARGET_URL: "/internal_/search",
  PATHNAME_KEY: "_p-p",
};

export const blogsInternalUrlQueryParams = {
  QUERY_KEY: "_b-l",
  QUERY_VALUE: "d",
  TARGET_URL: "/internal_/blogs",
  PATHNAME_KEY: "_b-p",
};

export const pdpInternalUrlQueryParams = {
  QUERY_KEY: "_p-v",
  QUERY_VALUE: "d",
};

export const surveryForms = {
  ORDER_CANCEL: "ORDER_CANCEL",
};

export const surveryFormCodes: { [key: string]: string } = {
  ORDER_CANCEL_EN: "mHNLZxhV5Wu-MTLvGBVD",
  ORDER_CANCEL_AR: "KBIh3QRIkKWOig79-3F_",
};

export const saleorConsentTemplateCode: { [key: string]: string } = {
  MARKETING: "MKT_{{country}}_EMAIL",
  USER_INFO: "MKT_{{country}}_PERS",
};

export const availableInquiryTypes = [
  {
    id: "PRODUCT_INQUIRY",
    value: {
      en: "Product Inquiry",
      ar: "استفسار عن منتج",
    },
  },
  {
    id: "STORE_INQUIRY",
    value: {
      en: "Store Inquiry",
      ar: "استفسار عن متجر",
    },
  },
  {
    id: "M_CLUB",
    value: {
      en: "M Club",
      ar: "إم كلوب",
    },
  },
  {
    id: "COMPLAINT",
    value: {
      en: "Complaint",
      ar: "شكوى",
    },
  },
  {
    id: "FEEDBACK",
    value: {
      en: "Feedback",
      ar: "ملاحظات",
    },
  },
];

export const SEVER_LS_ERROR_CODE = "nfte";

export const productListingSeoPageKeys: { [key: string]: "CATEGORY" | "BRAND" | "COLLECTION" } = {
  c: "CATEGORY",
  b: "BRAND",
  cl: "COLLECTION",
};

//Attributes Used In PDP
export const pdpAttributes = {
  [ProductType.SUNGLASSES]: {
    productAttributeKeys: ["frameMaterialEnum", "shape", "parent_brand"],
    variantAttributeKeys: [
      "model",
      "colorCode",
      "sapEAN",
      "fcCodeEnum",
      "lenseColor",
      "frame_lens_material",
      "frame_lens_coating",
      "polarized",
      "mirror",
      "geo_fit",
      "lensSize",
      "tSize",
      "bridgeSize",
      "title",
      "product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "fStyleEnum",
      "full-description",
      "isReturnableInt",
      "vto_enabled",
      "geo_fit",
      "frame_lens_material",
      "photochromic",
      "gradient",
      "anti_reflective",
      "printed",
      "blue_light_cut",
      "solid_color",
      "material_composition",
      "frame_finish",
    ],
  },
  [ProductType.EYEGLASSES]: {
    productAttributeKeys: ["frameMaterialEnum", "shape", "parent_brand", "frameTypeEnum"],
    variantAttributeKeys: [
      "model",
      "colorCode",
      "genderEnum",
      "sapEAN",
      "fcCodeEnum",
      "lenseColor",
      "frame_lens_material",
      "frame_lens_coating",
      "geo_fit",
      "lensSize",
      "tSize",
      "bridgeSize",
      "title",
      "product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "fStyleEnum",
      "full-description",
      "isReturnableInt",
      "vto_enabled",
      "geo_fit",
      "frame_lens_material",
      "photochromic",
      "gradient",
      "anti_reflective",
      "printed",
      "blue_light_cut",
      "solid_color",
      "material_composition",
      "frame_finish",
    ],
  },
  [ProductType.ACC_BY_COLOR]: {
    productAttributeKeys: ["parent_brand", "acPrdCatgEnumCode", "acPrdMatEnumCode"],
    variantAttributeKeys: [
      "accColorFamilyEnumCode",
      "acGenderEnumCode",
      "sapEAN",
      "acModel",
      "title",
      "product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "full-description",
      "isReturnableInt",
    ],
  },
  [ProductType.ACC_BY_COLOR_BY_POWER]: {
    productAttributeKeys: ["parent_brand", "acPrdCatgEnumCode", "acPrdMatEnumCode"],
    variantAttributeKeys: [
      "accColorFamilyEnumCode",
      "acGenderEnumCode",
      "sapEAN",
      "acModel",
      "acGenderEnumCode",
      "title",
      "product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "acc_power",
      "full-description",
      "isReturnableInt",
    ],
  },
  [ProductType.ACC_BY_FRAMECOLOR_BY_LENSCOLOR]: {
    productAttributeKeys: ["parent_brand", "acPrdCatgEnumCode", "acPrdMatEnumCode", "clipon_style"],
    variantAttributeKeys: [
      "acGenderEnumCode",
      "sapEAN",
      "title",
      "product_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "clipon_framecolor",
      "clipon_lenscolor",
      "hard_coating",
      "uv_block",
      "polarized",
      "isReturnableInt",
    ],
  },
  [ProductType.ACC_VOLUME]: {
    productAttributeKeys: ["parent_brand", "acGenderEnumCode", "acPrdCatgEnumCode"],
    variantAttributeKeys: [
      "volume",
      "sapEAN",
      "volume",
      "title",
      "product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "full-description",
      "description",
      "isReturnableInt",
    ],
  },
  [ProductType.CL_CLEAR]: {
    productAttributeKeys: ["windEnum", "parent_brand", "clWater"],
    variantAttributeKeys: [
      "uvBlock",
      "clMatEnum",
      "frame_lens_coating",
      "clColorEnum",
      "clDiameter",
      "clBaseCurve",
      "clUsage",
      "title",
      "cl_product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "clPower",
      "full-description",
      "clPerPackSize",
      "isReturnableInt",
    ],
  },
  [ProductType.CL_COLOR]: {
    productAttributeKeys: ["windEnum", "parent_brand", "clWater"],
    variantAttributeKeys: [
      "uvBlock",
      "clMatEnum",
      "frame_lens_coating",
      "clColorEnum",
      "clDiameter",
      "clBaseCurve",
      "clUsage",
      "disclaimer",
      "cl_product_image",
      "model_image",
      "feature1",
      "feature2",
      "feature3",
      "feature4",
      "description",
      "title",
      "clPower",
      "clPerPackSize",
      "full-description",
      "isReturnableInt",
    ],
  },
};

export const skuRedirectionPageTypes = {
  PRODUCT: "product",
  COLLECTION: "collection",
};

export const defaultTranslations: { [key: string]: Record<string, string> } = {
  unit_lens_per_box: {
    en: "Lens/Box",
    ar: "عدسة/علبة",
  },
  unit_lenses_per_box: {
    en: "Lenses/Box",
    ar: "عدسات/علبة",
  },
};

export const BrandMainCategories = ["Sunglasses", "Eyeglasses", "Contact Lenses", "Accessories"];